import { Container } from './Container'
import { Header } from './Header'
import { MessageList } from './MessageList'
import { Composer } from './Composer/Composer'
import { useMemo, useEffect } from 'react'
import type {
  BlockMessage,
  WebchatError,
  Feedback,
  FileType,
  IntegrationMessage,
  Renderers,
  RichBlockMessage,
} from '../types'
import { Loader } from './Loader'
import { StylesheetProvider } from '../providers'
import { LatestWebchatConfig } from '@bpinternal/shared'
import { ErrorState } from './Error'
import { User } from '@botpress/webchat-client'
import { enrichMessage } from '../utils'

export type Props = {
  className?: string
  renderers?: Partial<Renderers>
  configuration: LatestWebchatConfig
  messages: BlockMessage[]
  participants: User[]
  connected?: boolean
  disableComposer?: boolean
  isReadOnly?: boolean
  isLoading?: boolean
  isTyping?: boolean
  lastTypingHeartbeat?: Date | null
  allowFileUpload?: boolean
  clientId?: string
  closeWindow?: () => void
  restartConversation?: () => void
  user?: { id: string } & Record<string, unknown>
  sendMessage?: (payload: IntegrationMessage['payload']) => Promise<void>
  uploadFile?: (file: File) => Promise<{ fileUrl: string; name: string; type: FileType }>
  addMessageFeedback?: (messageId: string, feedback: Feedback) => void
  triggerNetworkError?: (errorType?: 'network' | 'timeout' | 'server', delay?: number) => Promise<never>
  error?: WebchatError
  soundOn?: boolean
  toggleSound?: () => void
  storageKey?: string
}

export const Chat = ({
  connected,
  configuration,
  isTyping,
  messages,
  user,
  isLoading,
  renderers,
  isReadOnly,
  disableComposer,
  allowFileUpload,
  error,
  participants,
  closeWindow,
  sendMessage,
  uploadFile,
  addMessageFeedback,
  restartConversation,
  clientId,
  storageKey,
  soundOn,
  toggleSound,
  ...props
}: Props) => {
  const {
    color,
    fontFamily,
    radius,
    themeMode,
    variant,
    headerVariant,
    botAvatar,
    botDescription,
    botName,
    composerPlaceholder,
    footer,
    feedbackEnabled,
    additionalStylesheetUrl,
  } = configuration

  const themeProps = { color, fontFamily, radius, themeMode, variant, headerVariant, additionalStylesheetUrl }

  if (!feedbackEnabled) {
    addMessageFeedback = undefined
  }

  const enrichedMessages: RichBlockMessage[] = useMemo(() => {
    return user ? enrichMessage(messages, participants, user.id, botName, botAvatar) : []
  }, [botAvatar, botName, messages, user, participants])

  const isRecoverableConnectionError = (err?: WebchatError) => err?.type === 'network' || err?.type === 'disconnected'

  const attemptSelfHealIfOnline = () => {
    if (
      isRecoverableConnectionError(error) &&
      typeof navigator !== 'undefined' &&
      navigator.onLine &&
      restartConversation
    ) {
      restartConversation()
    }
  }

  useEffect(() => {
    if (!restartConversation) return

    window.addEventListener('online', attemptSelfHealIfOnline)

    return () => {
      window.removeEventListener('online', attemptSelfHealIfOnline)
    }
  }, [error, restartConversation])

  useEffect(() => {
    if (!isRecoverableConnectionError(error) || !restartConversation) return

    const intervalId = window.setInterval(() => {
      attemptSelfHealIfOnline()
    }, 3000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [error, restartConversation])

  return (
    <>
      <StylesheetProvider {...themeProps} />
      <Container
        connected={connected}
        allowFileUpload={allowFileUpload}
        uploadFile={uploadFile}
        storageKey={storageKey}
        {...props}
      >
        {error ? (
          <ErrorState error={error} restartConversation={restartConversation} closeWindow={closeWindow} />
        ) : (
          <>
            <Header
              closeWindow={closeWindow}
              configuration={configuration}
              restartConversation={restartConversation}
              clientId={clientId}
              soundOn={soundOn}
              toggleSound={toggleSound}
            />
            {isLoading ? (
              <Loader />
            ) : (
              <MessageList
                {...{ botAvatar, botDescription, botName, renderers, isTyping, sendMessage, addMessageFeedback }}
                messages={enrichedMessages}
              />
            )}
            <Composer
              {...{
                sendMessage,
                uploadFile,
                connected,
                isReadOnly,
                disableComposer,
                footer,
                composerPlaceholder,
                allowFileUpload,
                storageKey,
              }}
            />
          </>
        )}
      </Container>
    </>
  )
}
